import { useEffect, useRef, useState } from "react";

// tier thresholds ($ 24h volume) — easy to adjust
export const STORM_THRESHOLDS = [100000, 500000, 2000000];
export const STORM_LEVELS = ["CALM", "BREWING", "STORM", "RAGING"];

export const stormLevelOf = (vol) =>
  vol >= STORM_THRESHOLDS[2] ? 3 : vol >= STORM_THRESHOLDS[1] ? 2 : vol >= STORM_THRESHOLDS[0] ? 1 : 0;

// flash cadence per level: [minDelay, maxDelay] ms
const CADENCE = [
  [12000, 22000],
  [6000, 11000],
  [2600, 5200],
  [800, 2000],
];

const makeBoltPath = (h) => {
  let x = 0;
  let y = 0;
  const pts = [`M0,0`];
  const steps = 6 + Math.floor(Math.random() * 4);
  for (let i = 0; i < steps; i++) {
    x += (Math.random() - 0.5) * 46;
    y += h / steps;
    pts.push(`L${x.toFixed(0)},${y.toFixed(0)}`);
  }
  return pts.join(" ");
};

export const StormLayer = ({ level }) => {
  const [flashes, setFlashes] = useState([]);
  const levelRef = useRef(level);
  levelRef.current = level;

  useEffect(() => {
    let alive = true;
    let t;
    const isMobile = window.innerWidth < 640;
    const fire = () => {
      if (!alive) return;
      const lv = levelRef.current;
      const id = Date.now() + Math.random();
      const withBolt = lv >= 2 && (lv === 3 || Math.random() > 0.4);
      const flash = {
        id,
        x: 8 + Math.random() * 84,
        strength: (0.25 + 0.2 * lv) * (0.8 + Math.random() * 0.4) * (isMobile ? 0.8 : 1),
        bolt: withBolt && !(isMobile && lv < 3) ? makeBoltPath(280 + Math.random() * 200) : null,
        branch: lv >= 2 ? makeBoltPath(160) : null,
      };
      setFlashes((fs) => [...fs.slice(-4), flash]);
      setTimeout(() => setFlashes((fs) => fs.filter((f) => f.id !== id)), 650);
      const [mn, mx] = CADENCE[levelRef.current];
      t = setTimeout(fire, mn + Math.random() * (mx - mn));
    };
    const [mn, mx] = CADENCE[level];
    t = setTimeout(fire, mn * 0.3 + Math.random() * (mx - mn) * 0.5);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  return (
    <div className="fixed inset-0 z-[1] pointer-events-none overflow-hidden" data-testid="storm-layer" data-storm-level={level}>
      {flashes.map((f) => (
        <span key={f.id}>
          <span
            className="storm-flash absolute inset-0"
            style={{
              background: `radial-gradient(ellipse at ${f.x}% 0%, rgba(200,180,255,${f.strength}), transparent 55%)`,
            }}
          />
          {f.bolt && (
            <svg
              className="storm-bolt-svg absolute top-0"
              style={{ left: `${f.x}%` }}
              width="120"
              height="520"
              viewBox="-60 0 120 520"
              aria-hidden="true"
            >
              <path d={f.bolt} stroke="#e9dcff" strokeWidth="2.4" fill="none" strokeLinejoin="round" />
              {f.branch && <path d={f.branch} transform="translate(0,120) rotate(28)" stroke="#00FFD1" strokeWidth="1.4" fill="none" />}
            </svg>
          )}
        </span>
      ))}
      <div className="storm-edge absolute inset-0" style={{ opacity: level === 3 ? 1 : 0 }} data-testid="storm-edge-glow" />
    </div>
  );
};
